// The last line of defence against losing work.
//
// The server is the authority and snapshots to S3 within seconds, but there is
// a window it cannot cover: everything drawn while this browser cannot reach
// it. Wifi drops on a train, a laptop sleeps mid-sentence, the task is rolling
// during a deploy. Those edits exist only in this tab, and closing it would end
// them.
//
// So while anything is unconfirmed, the whole local scene is mirrored into
// IndexedDB. On the next open, whatever the server has not caught up with is
// merged back in and re-sent (shared/recovery.js decides what that is, using
// the same comparison as live reconciliation, so a backup can never undo
// somebody else's newer work).
//
// Every call here fails soft. A browser with IndexedDB blocked — private mode,
// a locked-down profile — loses the safety net, not the app.

const DB_NAME = "hivemind-canvas";
const DB_VERSION = 1;
const STORE = "unsaved-scenes";

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "boardId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
};

const run = async (mode, work) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    let tx;
    try {
      tx = db.transaction(STORE, mode);
    } catch {
      resolve(null);
      return;
    }
    const request = work(tx.objectStore(STORE));
    tx.onabort = () => resolve(null);
    tx.onerror = () => resolve(null);
    if (request) request.onsuccess = () => resolve(request.result ?? null);
    else tx.oncomplete = () => resolve(null);
  });
};

/** Mirror the current scene. Called only while something is unconfirmed. */
export const saveBackup = async (boardId, elements) =>
  run("readwrite", (store) =>
    store.put({ boardId, elements, savedAt: new Date().toISOString() }),
  );

/** @returns {Promise<{boardId, elements, savedAt}|null>} */
export const readBackup = async (boardId) => run("readonly", (store) => store.get(boardId));

/** Drop it once the server has confirmed everything is on disk. */
export const clearBackup = async (boardId) => run("readwrite", (store) => store.delete(boardId));
